import { useState, useMemo, useEffect, useRef } from "react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "../../ui/table";
import {
    UserPlus,
    Search,
    ChevronLeft,
    ChevronRight,
    Lock,
    Users,
    Calendar,
    Clock,
    Shield,
    Eye,
    EyeOff,
    Tags,
    MoreHorizontal,
    Ban,
    Trash2,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import InviteUserDialog from "./InviteUserDialog";
import { deleteInvitation, fetchAllDevices, fetchAllRoles, fetchPendingUsers, revokeInvitation } from "./Api/inviteuserdetails";
import { myLocalStorage } from "../../StorageHelper";
import { Role, UserVerification } from "./interface";
import { Device } from "../interfaces/EndpointInterfaces";
import { formatDate } from "../formatTimeAgo";
import EmptyStateCard from "../EmptyStateCard";
import useUserStore from "../../../services/userStore";
import {
    LIST_STATE_KEYS,
    parsePersistedNumber,
    parsePersistedString,
    readPersistedListState,
    writePersistedListState,
} from "../../../utils/listPaginationState";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../ui/alert-dialog";
import { useToast } from "../../../hooks/use-toast";


const ITEMS_PER_PAGE = 10;

export default function InviteUserDetails() {
    const savedState = readPersistedListState(LIST_STATE_KEYS.inviteUsers);
    const [searchTerm, setSearchTerm] = useState(parsePersistedString(savedState.searchTerm));
    const [currentPage, setCurrentPage] = useState(parsePersistedNumber(savedState.currentPage, 1));
    const [showInviteDialog, setShowInviteDialog] = useState(false);
    const [activeTab, setActiveTab] = useState(parsePersistedString(savedState.activeTab, "user"));
    const skipTabPageResetRef = useRef(true);
    const tenantName = myLocalStorage.getItem("latestTenant").tenantName || "";
    const [users, setUsers] = useState<any[]>([]);
    const [allDevices, setAllDevices] = useState<Device[]>([]);
    const [visibleCodes, setVisibleCodes] = useState<Set<string>>(new Set());
    const [adminRoles, setAdminRoles] = useState<Role[] | []>([])
    const [userRoles, setUserRoles] = useState<Role[] | []>([])
    const { userRole } = useUserStore((state) => state.user);
    const { toast } = useToast();
    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserVerification | null>(null);
    const [actionLoading, setActionLoading] = useState(false);


    // Filter users based on search and tab
    const filteredUsers = useMemo(() => {
        const tabFiltered = users.filter(user =>
            activeTab === "admin" ? user.type === "admin" : user.type === "user"
        );

        if (!searchTerm) return tabFiltered;

        const searchLower = searchTerm.toLowerCase();
        return tabFiltered.filter(user =>
            user.firstName.toLowerCase().includes(searchLower) ||
            user.lastName.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower)
        );
    }, [searchTerm, activeTab, users]);

    // Paginate filtered users
    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredUsers.slice(startIndex, endIndex);
    }, [filteredUsers, currentPage]);

    const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const getRoleBadgeVariant = (role: string) => {
        if (role.includes('admin')) return 'default';
        if (role.includes('epam')) return 'secondary';
        return 'outline';
    };

    const getTagBadgeVariant = (tag: string) => {
        if (tag.includes('prod')) return 'destructive';
        if (tag.includes('dev')) return 'default';
        return 'secondary';
    };

    const isExpiringSoon = (expiresAt: string) => {
        const expiry = parseExpiryDate(expiresAt);
        if (!expiry) return false;
        return expiry.getTime() - nowTick < 24 * 60 * 60 * 1000;
    };

    // Function to toggle visibility
    const toggleVerificationCode = (userId: string) => {
        setVisibleCodes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };


    const generateVerificationCode = (user: UserVerification) => {
        return user.verificationCode;
    };

    const [nowTick, setNowTick] = useState(Date.now());

    useEffect(() => {
        const interval = setInterval(() => setNowTick(Date.now()), 60_000);
        return () => clearInterval(interval);
    }, []);

    const parseExpiryDate = (expiresAt?: string) => {
        if (!expiresAt) return null;
        const trimmed = expiresAt.trim();
        const dotted = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2})/);
        if (dotted) {
            return new Date(`${dotted[3]}-${dotted[2]}-${dotted[1]}T${dotted[4]}:${dotted[5]}:00`);
        }
        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const getHoursRemaining = (expiresAt: string) => {
        const expiry = parseExpiryDate(expiresAt);
        if (!expiry) return 0;
        const diff = expiry.getTime() - nowTick;
        return Math.max(0, Math.round(diff / (1000 * 60 * 60)));
    };

    const formatHoursRemaining = (hours: number) => {
        if (hours <= 0) return 'Expired';
        if (hours < 24) return `${hours}h`;
        const days = Math.floor(hours / 24);
        const remainingHours = hours % 24;
        return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
    };

    const getExpiryVariant = (hours: number) => {
        if (hours <= 0) return 'destructive';
        if (hours < 24) return 'outline';
        return 'secondary';
    };

    const getAllDevices = async () => {
        const devices: Device[] = await fetchAllDevices(tenantName);
        setAllDevices(devices);
    }

    const loadUsers = async () => {
        const pending: UserVerification[] = await fetchPendingUsers(tenantName);
        const roles: Role[] = await fetchAllRoles(tenantName);

        setAdminRoles(roles.filter(role => role.roleType === "admin") || []);
        setUserRoles(roles.filter(role => role.roleType !== "admin" && role.roleName != "super-user") || [])

        const usersWithTypeRolesAndTags = pending.map(user => {
            const roleNames = user.roleName.split(",").map(r => r.trim());
            const machines = (user.machines || "").split(",").map(r => r.trim());
            const isAdmin = roleNames.some(r =>
                roles.find(role => role.roleName === r && role.roleType === "admin")
            );

            const tagList = user.tags ? user.tags.split(",").map(t => t.trim()).filter(Boolean) : [];

            return {
                ...user,
                type: isAdmin ? "admin" : "user",
                roles: roleNames,
                tags: tagList,
                machines: machines
            };
        });

        setUsers(usersWithTypeRolesAndTags);
    };


    useEffect(() => {
        if (!tenantName) return;
        loadUsers();
        getAllDevices();
    }, [tenantName])

    useEffect(() => {
        writePersistedListState(LIST_STATE_KEYS.inviteUsers, {
            currentPage,
            searchTerm,
            activeTab,
        });
    }, [currentPage, searchTerm, activeTab]);

    useEffect(() => {
        if (skipTabPageResetRef.current) {
            skipTabPageResetRef.current = false;
            return;
        }
        setCurrentPage(1);
    }, [activeTab]);

    const handleRevokeClick = (user: UserVerification) => {
        setSelectedUser(user);
        setRevokeDialogOpen(true);
    };

    const handleDeleteClick = (user: UserVerification) => {
        setSelectedUser(user);
        setDeleteDialogOpen(true);
    };

    const handleRevokeConfirm = async () => {
        if (!selectedUser || !tenantName) return;
        setActionLoading(true);
        try {
            const response = await revokeInvitation(tenantName, selectedUser.email);
            if (response?.isOk) {
                toast({
                    title: "Invitation Revoked",
                    description: `Revoked invitation for ${selectedUser.email}`,
                });
                await loadUsers();
            } else {
                toast({
                    title: "Revoke Failed",
                    description: response?.errorMessage === "INVITATION_NOT_FOUND"
                        ? "Invitation not found or already removed."
                        : "Failed to revoke invitation.",
                    variant: "destructive",
                });
            }
        } catch (error) {
            console.error("Failed to revoke invitation:", error);
            toast({
                title: "Revoke Failed",
                description: "Failed to revoke invitation.",
                variant: "destructive",
            });
        } finally {
            setActionLoading(false);
            setRevokeDialogOpen(false);
            setSelectedUser(null);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!selectedUser || !tenantName) return;
        setActionLoading(true);
        try {
            const response = await deleteInvitation(tenantName, selectedUser.email);
            if (response?.isOk) {
                toast({
                    title: "Invitation Deleted",
                    description: `Deleted invitation for ${selectedUser.email}`,
                });
                await loadUsers();
            } else {
                toast({
                    title: "Delete Failed",
                    description: response?.errorMessage === "INVITATION_NOT_FOUND"
                        ? "Invitation not found or already removed."
                        : "Failed to delete invitation.",
                    variant: "destructive",
                });
            }
        } catch (error) {
            console.error("Failed to delete invitation:", error);
            toast({
                title: "Delete Failed",
                description: "Failed to delete invitation.",
                variant: "destructive",
            });
        } finally {
            setActionLoading(false);
            setDeleteDialogOpen(false);
            setSelectedUser(null);
        }
    };


    return (
        <div className="flex-1 space-y-6 p-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                        <UserPlus className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-invite-users-title">
                            Invite Users
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Manage pending user invitations and invite new users to the platform
                        </p>
                    </div>
                </div>
                <Button
                    onClick={() => setShowInviteDialog(true)}
                    className="gap-2"
                    data-testid="button-invite-now"
                >
                    <UserPlus className="w-4 h-4" />
                    Invite User
                </Button>
            </div>

            {/* Main Content */}
            <Card className="border-border/40 hover-elevate cursor-pointer transition-transform hover:scale-[1.01]">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Users className="w-5 h-5" />
                                Pending User Verifications ({filteredUsers.length})
                            </CardTitle>
                            <div className="flex items-center gap-4">
                                {userRole !== "epam-admin" &&
                                    userRole !== "server-pam-admin" &&
                                    <div className="flex bg-muted/50 rounded-lg p-1">
                                        <button
                                            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${activeTab === "admin"
                                                ? "bg-blue-500 text-white shadow-sm"
                                                : "text-muted-foreground hover:text-foreground"
                                                }`}
                                            onClick={() => setActiveTab("admin")}
                                            data-testid="tab-admin-invites"
                                        >
                                            <Shield className="w-4 h-4" />
                                            Admin
                                        </button>
                                        <button
                                            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${activeTab === "user"
                                                ? "bg-blue-500 text-white shadow-sm"
                                                : "text-muted-foreground hover:text-foreground"
                                                }`}
                                            onClick={() => setActiveTab("user")}
                                            data-testid="tab-user-invites"
                                        >
                                            <Users className="w-4 h-4" />
                                            Users
                                        </button>
                                    </div>
                                }
                                <div className="relative w-64">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                    <Input
                                        placeholder="Search users..."
                                        value={searchTerm}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                        className="pl-10"
                                        data-testid="input-search-users"
                                    />
                                </div>
                            </div>
                        </div>
                    </CardHeader>
                    <TabsContent value={activeTab} className="mt-0">

                        <CardContent className="p-0">
                            {paginatedUsers.length > 0 ? (
                                <div className="border-t">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-muted/30">
                                                <TableHead className="font-semibold">
                                                    <div className="flex items-center gap-1">
                                                        <Calendar className="w-4 h-4" />
                                                        Created At
                                                    </div>
                                                </TableHead>
                                                <TableHead className="font-semibold">Name</TableHead>
                                                <TableHead className="font-semibold">Email</TableHead>
                                                <TableHead className="font-semibold text-center">
                                                    <div className="flex items-center justify-center gap-1">
                                                        <Lock className="w-4 h-4" />
                                                        Verification Code
                                                    </div>
                                                </TableHead>
                                                <TableHead className="font-semibold">Roles</TableHead>
                                                {activeTab === "admin" && <TableHead className="font-semibold">Tags</TableHead>}
                                                {activeTab === "user" && <TableHead className="font-semibold">Machines</TableHead>}
                                                <TableHead className="font-semibold text-center">
                                                    <div className="flex items-center justify-center gap-1">
                                                        <Clock className="w-4 h-4" />
                                                        Expires In
                                                    </div>
                                                </TableHead>
                                                <TableHead className="font-semibold text-center">Actions</TableHead>

                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {paginatedUsers.map((user) => {
                                                const hoursRemaining = getHoursRemaining(user.expiredAt);
                                                return (
                                                    <TableRow
                                                        key={user.email}
                                                        className="hover:bg-muted/30 transition-colors"
                                                        data-testid={`row-user-${user.id}`}
                                                    >
                                                        <TableCell data-testid={`created-at-${user.id}`}>
                                                            <div className="text-sm text-muted-foreground font-medium">
                                                                {formatDate(user.createdAt)}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex flex-col">
                                                                <span className="font-medium" data-testid={`name-${user.id}`}>
                                                                    {user.firstName} {user.lastName}
                                                                </span>
                                                                <span className="text-xs text-muted-foreground capitalize">
                                                                    {user.type}
                                                                </span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell data-testid={`email-${user.id}`}>
                                                            <div className="font-mono text-sm">{user.email}</div>
                                                        </TableCell>
                                                        <TableCell className="text-center">
                                                            <div className="flex items-center justify-center gap-2">
                                                                {visibleCodes.has(user.email) ? (
                                                                    <>
                                                                        <span className="font-mono text-sm font-medium bg-muted px-2 py-1 rounded" data-testid={`verification-code-${user.id}`}>
                                                                            {generateVerificationCode(user)}
                                                                        </span>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-6 w-6 p-0"
                                                                            onClick={() => toggleVerificationCode(user.email)}
                                                                            aria-label="Hide verification code"
                                                                            data-testid={`hide-code-${user.id}`}
                                                                        >
                                                                            <EyeOff className="w-3 h-3" />
                                                                        </Button>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Shield className="w-4 h-4 text-muted-foreground" />
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-6 w-6 p-0 hover:bg-primary/10"
                                                                            onClick={() => toggleVerificationCode(user.email)}
                                                                            aria-label="Show verification code"
                                                                            data-testid={`show-code-${user.id}`}
                                                                        >
                                                                            <Eye className="w-3 h-3" />
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex flex-wrap gap-1">
                                                                {user.roles?.map((role: string) => (
                                                                    <Badge
                                                                        key={role}
                                                                        className="text-xs bg-blue-500 text-white hover:bg-blue-600"
                                                                        data-testid={`badge-role-${role}`}
                                                                    >
                                                                        {role}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        </TableCell>
                                                        {activeTab === "admin" && (
                                                            <TableCell>
                                                                <div className="flex flex-wrap gap-1">
                                                                    {user.tags && user.tags.length > 0 ? (
                                                                        user.tags?.map((tag: any) => (
                                                                            <Badge
                                                                                key={tag}
                                                                                className="text-xs bg-blue-600 text-white dark:bg-blue-500 dark:text-white border border-blue-600 dark:border-blue-500 gap-1"
                                                                                data-testid={`badge-tag-${tag}`}
                                                                            >
                                                                                <Tags className="w-3 h-3" />
                                                                                {tag}
                                                                            </Badge>
                                                                        ))
                                                                    ) : (
                                                                        <span className="text-muted-foreground text-xs">-</span>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                        )}
                                                        {activeTab === "user" && (
                                                            <TableCell data-testid={`machines-${user.id}`}>
                                                                {user.machines && user.machines.length > 0 ? (
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {user.machines.slice(0, 2).map((machine: any) => (
                                                                            <Badge
                                                                                key={machine}
                                                                                variant="outline"
                                                                                className="text-xs font-mono"
                                                                                data-testid={`badge-machine-${machine}`}
                                                                            >
                                                                                {machine}
                                                                            </Badge>
                                                                        ))}
                                                                        {user.machines.length > 2 && (
                                                                            <Badge variant="secondary" className="text-xs">
                                                                                +{user.machines.length - 2} more
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted-foreground text-xs">-</span>
                                                                )}
                                                            </TableCell>
                                                        )}
                                                        <TableCell className="text-center" data-testid={`expires-in-${user.id}`}>
                                                            <Badge
                                                                variant={getExpiryVariant(hoursRemaining)}
                                                                className="text-xs font-medium"
                                                            >
                                                                {hoursRemaining <= 0 ? 'Expired' : `${formatHoursRemaining(hoursRemaining)}`}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-center" data-testid={`actions-${user.email}`}>
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-8 w-8 p-0"
                                                                        data-testid={`button-actions-${user.email}`}
                                                                    >
                                                                        <MoreHorizontal className="h-4 w-4" />
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">

                                                                    <DropdownMenuItem
                                                                        onClick={() => handleDeleteClick(user)}
                                                                        className="text-destructive"
                                                                        disabled={actionLoading}
                                                                        data-testid={`button-delete-${user.email}`}
                                                                    >
                                                                        <Trash2 className="mr-2 h-4 w-4" />
                                                                        Revoke Invitation
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="p-6 border-t">
                                    <EmptyStateCard
                                        icon={UserPlus}
                                        title={searchTerm ? "No matching invitations found" : "No pending invitations"}
                                        description={
                                            searchTerm
                                                ? "Try adjusting your search criteria."
                                                : "Invite users to get started with the platform."
                                        }
                                    />
                                </div>
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-between p-4 border-t bg-muted/20">
                                    <div className="text-sm text-muted-foreground">
                                        Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length} invitations
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage <= 1}
                                            className="gap-1"
                                            data-testid="button-prev-page"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                            Previous
                                        </Button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                const page = i + 1;
                                                return (
                                                    <Button
                                                        key={page}
                                                        variant={currentPage === page ? "default" : "outline"}
                                                        size="sm"
                                                        onClick={() => handlePageChange(page)}
                                                        className="w-8 h-8 p-0"
                                                        data-testid={`button-page-${page}`}
                                                    >
                                                        {page}
                                                    </Button>
                                                );
                                            })}
                                            {totalPages > 5 && (
                                                <>
                                                    <span className="text-muted-foreground px-2">...</span>
                                                    <Button
                                                        variant={currentPage === totalPages ? "default" : "outline"}
                                                        size="sm"
                                                        onClick={() => handlePageChange(totalPages)}
                                                        className="w-8 h-8 p-0"
                                                        data-testid={`button-page-${totalPages}`}
                                                    >
                                                        {totalPages}
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage >= totalPages}
                                            className="gap-1"
                                            data-testid="button-next-page"
                                        >
                                            Next
                                            <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </TabsContent>
                </Tabs>
            </Card>

            <InviteUserDialog
                open={showInviteDialog}
                onOpenChange={setShowInviteDialog}
                availableAdminRoles={adminRoles}
                availableUserRoles={userRoles}
                availableMachines={allDevices}
                tenantName={tenantName}
                pendingUserCall={loadUsers}
                handleTab={(value: string) => { setActiveTab(value) }}
            />

            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Revoke Invitation</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to revoke the invitation for{" "}
                            <span className="font-semibold">
                                {selectedUser?.firstName} {selectedUser?.lastName}
                            </span>
                            ? The verification code will no longer work and the user will not be able to complete signup.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={actionLoading} data-testid="button-cancel-delete">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteConfirm}
                            disabled={actionLoading}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="button-confirm-delete"
                        >
                            Revoke Invitation
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}